import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
    AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
    Box, Typography, CircularProgress, IconButton, Dialog, DialogContent, DialogTitle, 
    useTheme, useMediaQuery, Paper, Stack
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import PeopleIcon from "@mui/icons-material/People";
import StorefrontIcon from "@mui/icons-material/Storefront";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { getRegistrationStats } from "../../services/catalogServices";

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
        return (
            <Paper
                elevation={4}
                sx={{
                    p: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(4px)",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0"
                }}
            >
                <Typography variant="caption" display="block" sx={{ color: "#64748b", fontWeight: "bold", mb: 1, textTransform: "uppercase" }}>
                    Periodo: {label}
                </Typography>
                <Stack spacing={1}>
                    {payload.map((entry) => (
                        <Box key={entry.name} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: entry.color }} />
                            <Typography variant="body2" sx={{ color: "#1e293b", fontWeight: 600 }}>
                                {entry.name === "userCount" ? "Utenti:" : "Vendor:"}
                            </Typography>
                            <Typography variant="body2" sx={{ ml: "auto", fontWeight: 700, color: "#0b3d87" }}>
                                {entry.value}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Paper>
        );
    }
    return null;
};

CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.number,
            color: PropTypes.string
        })
    ),
    label: PropTypes.string
};

const LegendContent = ({ payload }) => (
    <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 2 }}>
        {payload.map((entry) => (
            <Box key={entry.value} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {entry.value === "userCount" ? <PeopleIcon sx={{ fontSize: 18, color: "#3b82f6" }} /> : <StorefrontIcon sx={{ fontSize: 18, color: "#10b981" }} />}
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {entry.value === "userCount" ? "Utenti" : "Vendor"}
                </Typography>
            </Box>
        ))}
    </Stack>
);

LegendContent.propTypes = {
    payload: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            color: PropTypes.string
        })
    )
};

const GraficoRegistrazioni = ({ filtroGlobal, startDateGlobal, endDateGlobal }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openFullscreen, setOpenFullscreen] = useState(false);

    const theme = useTheme();
    const fullScreenModal = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchData = useCallback(async () => {
        if (filtroGlobal === "custom" && (!startDateGlobal || !endDateGlobal)) return;

        setLoading(true);
        setError(null);
        try {
            const response = await getRegistrationStats(filtroGlobal, null, startDateGlobal, endDateGlobal);
            if (response?.points) {
                setData(response.points);
            } else {
                setData([]);
            }
        } catch (err) {
            setError(err.message || "Non è stato possibile recuperare i dati.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [filtroGlobal, startDateGlobal, endDateGlobal]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const renderChart = (isFullscreen = false) => {
        let chartHeight = isFullscreen ? 500 : "100%";

        return (
            <ResponsiveContainer width="100%" height={chartHeight}>
                <AreaChart data={data} margin={{ top: 10, right: isFullscreen ? 30 : 40, left: 0, bottom: isFullscreen ? 0 : 30 }}>
                    <defs>
                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorVendors" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                        dataKey="period"
                        tick={{ fontSize: isFullscreen ? 13 : 11, fontWeight: 500, fill: "#64748b" }}
                        minTickGap={20}
                        axisLine={false}
                        tickLine={false}
                        dy={10}
                    />
                    <YAxis
                        tick={{ fontSize: isFullscreen ? 13 : 11, fontWeight: 500, fill: "#64748b" }}
                        axisLine={false}
                        tickLine={false}
                        dx={-5}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '5 5' }} />
                    <Legend
                        verticalAlign="top"
                        height={36}
                        content={<LegendContent />}
                    />
                    <Area
                        type="monotone"
                        dataKey="userCount"
                        stroke="#3b82f6"
                        fillOpacity={1}
                        fill="url(#colorUsers)"
                        strokeWidth={3}
                        dot={{ r: 0 }}
                        activeDot={{ r: 6, strokeWidth: 0, fill: "#3b82f6" }}
                        name="userCount"
                    />
                    <Area
                        type="monotone"
                        dataKey="vendorCount"
                        stroke="#10b981"
                        fillOpacity={1}
                        fill="url(#colorVendors)"
                        strokeWidth={3}
                        dot={{ r: 0 }}
                        activeDot={{ r: 6, strokeWidth: 0, fill: "#10b981" }}
                        name="vendorCount"
                    />
                </AreaChart>
            </ResponsiveContainer>
        );
    };

    let chartContent;
    if (loading) {
        chartContent = (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", minHeight: "200px" }}>
                <CircularProgress size={40} thickness={4} sx={{ color: "#0b3d87" }} />
            </Box>
        );
    } else if (error) {
        chartContent = (
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "200px",
                color: "#94a3b8"
            }}>
                <QueryStatsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Non è stato possibile recuperare i dati
                </Typography>
            </Box>
        );
    } else if (data.length === 0) {
        chartContent = (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", minHeight: "200px" }}>
                <Typography color="textSecondary" sx={{ fontWeight: 500 }}>Nessun dato disponibile.</Typography>
            </Box>
        );
    } else {
        chartContent = renderChart();
    }

    return (
        <Box sx={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ flexGrow: 1, position: "relative", minHeight: isMobile ? "200px" : "260px" }}>
                {chartContent}

                <IconButton
                    size="small"
                    onClick={() => setOpenFullscreen(true)}
                    title="Ingrandisci"
                    sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 10,
                        backgroundColor: "#f1f5f9",
                        borderRadius: "10px",
                        padding: "6px",
                        zIndex: 10,
                        "&:hover": { backgroundColor: "#e2e8f0" }
                    }}
                >
                    <FullscreenIcon fontSize="small" sx={{ color: "#0b3d87" }} />
                </IconButton>
            </Box>

            <Dialog
                fullScreen={fullScreenModal}
                open={openFullscreen}
                onClose={() => setOpenFullscreen(false)}
                maxWidth="lg"
                fullWidth
                slotProps={{
                    paper: { sx: { borderRadius: fullScreenModal ? 0 : "24px", overflow: "hidden" } }
                }}
            >
                <DialogTitle sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    color: "#0b3d87",
                    borderBottom: "1px solid #f1f5f9",
                    px: 3,
                    py: 2
                }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Analisi Registrazioni
                    </Typography>
                    <IconButton onClick={() => setOpenFullscreen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 4, backgroundColor: "#fdfdfd" }}>
                    <Box sx={{ mt: 2, height: "500px", width: "100%" }}>
                        {renderChart(true)}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

GraficoRegistrazioni.propTypes = {
    filtroGlobal: PropTypes.string,
    startDateGlobal: PropTypes.string,
    endDateGlobal: PropTypes.string
};

export default GraficoRegistrazioni;
 